// Browser side of the observability layer. Imported FIRST in index.js: the
// workforce bridge (lib/workforceBff.js) captures window.fetch lazily, so the
// final order is bridge -> this wrapper -> native fetch, and every same-origin
// /api and /sb call (raw fetch, local wrappers, supabase-js) gets a request id.
// Reports go to POST /api/client-log and never carry bodies, query strings,
// tokens, file names or photos.

const REPORT_PATH = '/api/client-log';
const RING_MAX = 20;
const ID_FIELDS = ['client_attempt_key', 'job_id', 'job_stage_id', 'inspection_id', 'design_job_id',
  'session_id', 'status', 'code', 'host', 'rid', 'count'];
// One hot error (a throwing mousemove handler) must not spend the shared
// per-IP server limit of every tester behind the same office NAT.
const DEDUPE_MS = 30_000;
const BUDGET_PER_MINUTE = 10;

const recent = [];
let extraHeaders = {};
let reporting = false;
const lastSent = new Map(); // report JSON without the stacks -> { t, rid }
let budget = { start: 0, used: 0 };
let suppressed = 0;

function newRequestId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* insecure context */ }
  let out = '';
  for (let i = 0; i < 32; i += 1) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

// The CRA main chunk hash; a stale service-worker bundle shows up as another hash.
function readBuild() {
  try {
    for (const script of document.querySelectorAll('script[src]')) {
      const match = /\/static\/js\/main\.([A-Za-z0-9]+)\.js/.exec(script.getAttribute('src') || '');
      if (match) return match[1];
    }
  } catch { /* no DOM */ }
  return 'dev';
}
const BUILD = readBuild();

const cut = (value, max) => (typeof value === 'string' ? value.slice(0, max) : undefined);

function apiPath(input) {
  try {
    const url = new URL(typeof input?.url === 'string' ? input.url : String(input), window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname.startsWith('/api/') || url.pathname.startsWith('/sb/') ? url.pathname : null;
  } catch { return null; }
}

function remember(m, p, s, rid, t0) {
  recent.push({ t: Date.now(), m, p, s, rid, ms: Date.now() - t0 });
  if (recent.length > RING_MAX) recent.shift();
}

function installFetchWrapper() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const nativeFetch = window.fetch;
  window.fetch = function fetchWithRequestId(input, init) {
    const path = apiPath(input);
    if (!path) return nativeFetch.call(window, input, init);
    const headers = new Headers(init?.headers || (typeof input?.url === 'string' ? input.headers : undefined));
    if (!headers.has('X-Request-Id')) headers.set('X-Request-Id', newRequestId());
    if (!headers.has('X-Client-Build')) headers.set('X-Client-Build', BUILD);
    const rid = headers.get('X-Request-Id');
    const method = String(init?.method || input?.method || 'GET').toUpperCase();
    const t0 = Date.now();
    const pending = nativeFetch.call(window, input, { ...init, headers });
    if (path === REPORT_PATH) return pending;
    return Promise.resolve(pending).then((res) => {
      if (!res.ok) remember(method, path, res.status, rid, t0);
      return res;
    }, (error) => {
      // A caller's own abort (unmount, carrier switch) is not a failure.
      if (error?.name !== 'AbortError') remember(method, path, 0, rid, t0);
      throw error;
    });
  };
}

function pickIds(source) {
  const out = {};
  for (const key of ID_FIELDS) {
    const value = source?.[key];
    if (typeof value === 'string' || typeof value === 'number') out[key] = value;
  }
  return out;
}

// Returns the rid of the identical report sent in the last 30 s instead of
// sending it again; beyond the per-page budget a report is only counted. The
// count rides on the next report that goes out.
function admit(payload) {
  const now = Date.now();
  // The same error thrown from another line is still the same error.
  const { stack, component_stack: componentStack, ...identity } = payload;
  const key = JSON.stringify(identity);
  const prev = lastSent.get(key);
  if (prev && now - prev.t < DEDUPE_MS) { suppressed += 1; return { rid: prev.rid }; }
  if (now - budget.start >= 60_000) budget = { start: now, used: 0 };
  if (budget.used >= BUDGET_PER_MINUTE) { suppressed += 1; return { rid: newRequestId() }; }
  budget.used += 1;
  const rid = newRequestId();
  lastSent.set(key, { t: now, rid });
  if (lastSent.size > 50) lastSent.delete(lastSent.keys().next().value);
  return { rid, send: true };
}

function send(payload) {
  const { rid, send: go } = admit(payload);
  if (!go || reporting) return rid;
  reporting = true;
  try {
    const held = suppressed;
    suppressed = 0;
    const body = JSON.stringify({
      ...payload,
      suppressed: held || undefined,
      route: window.location.pathname,
      build: BUILD,
      online: navigator.onLine,
      ua: cut(navigator.userAgent, 200),
      recent: recent.slice(),
    });
    Promise.resolve(window.fetch(REPORT_PATH, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { ...extraHeaders, 'Content-Type': 'application/json', 'X-Request-Id': rid },
      body,
    })).catch(() => {});
  } catch { /* reporting never throws */ } finally {
    reporting = false;
  }
  return rid;
}

/** Reports an error and returns the request id it was sent with (show shortCode(rid)). */
export function reportClientError(kind, error, extra = {}) {
  return send({
    kind,
    name: cut(error?.name, 60),
    message: cut(String(error?.message ?? error ?? ''), 500),
    stack: cut(error?.stack, 1500),
    component_stack: cut(extra.component_stack, 1000),
    ...pickIds(extra),
  });
}

/** Reports a named event with whitelisted ids only; returns its request id. */
export function reportClientEvent(name, fields = {}) {
  return send({ kind: 'event', name, ...pickIds(fields) });
}

/** Extra headers on reports only, e.g. the installer's X-Session-Token so the server can name the reporter. */
export function setClientLogHeaders(headers) {
  extraHeaders = headers && typeof headers === 'object' ? { ...headers } : {};
}

export function shortCode(rid) {
  return typeof rid === 'string' ? rid.slice(0, 8) : '';
}

export function lastFailedRequestId() {
  return recent.length ? recent[recent.length - 1].rid : null;
}

installFetchWrapper();
if (typeof window !== 'undefined') {
  // No error object means a cross-origin "Script error." or a browser notice: nothing to act on.
  window.addEventListener('error', (event) => { if (event.error != null) reportClientError('error', event.error); });
  window.addEventListener('unhandledrejection', (event) => { reportClientError('rejection', event.reason); });
}
